<template>
  <v-flex xs12>
    <data-source-table
        itemController="customer"
        itemIdentifyField="name"
        :headers="headers"
        :options.sync="options"
        :selected-items.sync="selectedItems"
        ref="dataSourceRef"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <confirm-dialog
              :is-open.sync="newCustomer.isOpened"
              width="400"
              @confirm="saveNew"
              confirm-button-text="save"
          >
            <template v-slot:activator>
              <v-btn
                  class="mx-2" color="green"
                  dark fab small
                  @click="newCustomer.isOpened = true"
              >
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" dark>
                      mdi-plus
                    </v-icon>
                  </template>
                  Add new
                </v-tooltip>
              </v-btn>
            </template>
            <template v-slot:title>
              Add new customer
            </template>
            <template v-slot:body>
              <v-container>
                <v-row>
                  <v-col>
                    <v-text-field
                        v-model="newCustomer.name"
                        label="name"
                        :rules="requiredRule"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col>
                    <v-text-field
                        v-model="newCustomer.full_name"
                        label="name"
                        :rules="requiredRule"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </template>
          </confirm-dialog>

          <v-spacer/>

          <v-btn
              class="mx-2" color="blue darken-1"
              outlined small
              @click="$refs.dataSourceRef ? $refs.dataSourceRef.openDialogSelectColumns() : false"
          >
            Select columns
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:additional.item.name="{ item }">
        <OpenItemPage
            item-route="customer"
            :item-id="item.id"
            tooltip-text="customer report"
        />
        <v-tooltip bottom v-if="item.is_ground">
          <template v-slot:activator="{ on, attrs }">
            <v-icon
                v-bind="attrs"
                v-on="on"
                x-small
                color="orange"
            >
              mdi-star
            </v-icon>
          </template>
          ground
        </v-tooltip>
      </template>

      <template v-slot:item.application.application_url="{ item }">
        <div
            v-if="item.application && item.application.application_url"
        >
          <a target="_blank" :href="item.application.application_url">
            {{ item.application.application_url }}
          </a>
        </div>
        <div v-else class="font-italic font-weight-thin red--text" v-text="'empty'"/>
      </template>

      <template v-slot:item.analytics="{ item }">
        <v-chip
            v-if="item.application_id && applicationAnalyticsCustomersList[item.application_id]"
            class="ma-0"
            :color="applicationAnalyticsCustomersList[item.application_id].is_airport
            ? '#FF6E40'
            : '#c49c18'"
            small
        >
          {{ applicationAnalyticsCustomersList[item.application_id].name || '' }}
          {{ applicationAnalyticsCustomersList[item.application_id].is_airport ? '(apt)' : '' }}
        </v-chip>
      </template>

    </data-source-table>
  </v-flex>
</template>

<script>
import { mapActions } from 'vuex';
import DataSourceTable from '@/components/DataSource/DataTable.vue';
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue';
import OpenItemPage from '@/components/OpenItemPage.vue';

export default {
  name: 'Customer',
  components: {
    OpenItemPage,
    ConfirmDialog,
    DataSourceTable,
  },
  data: () => ({
    requiredRule: [
      (v) => !!v || 'Required',
    ],
    newCustomer: {
      isOpened: false,
      name: '',
      full_name: '',
    },
    itemClone: {},
    customers: [],
    loading: true,
    options: { filters: {}, itemsPerPage: 10 },
    selectedItems: [],
    routemapList: [],
    applicationsList: [],
    applicationAnalyticsCustomersList: [],
  }),

  created() {
    this.$requestHelpers
      .get('api/admin/customer/routemap-list')
      .then((response) => {
        this.routemapList = response.data.result;
      });

    this.$requestHelpers
      .get('api/admin/customer/getApplicationsList')
      .then((response) => {
        this.applicationsList = response.data.result.map(
          // eslint-disable-next-line array-callback-return,max-len
          (item) => ({ application_id: parseInt(item.application_id, 10), name: item.name }),
        );
      });

    this.getAnalyticsCustomers();
  },

  methods: {
    ...mapActions({
      setSnack: 'snackbar/setState',
    }),

    saveNew() {
      this.$requestHelpers
        .post('api/admin/customer', this.newCustomer)
        .then((response) => {
          if (response.data.success) {
            this.$refs.dataSourceRef.addItem(response.data.result);
          }
        }).finally(() => {
          this.newCustomer.name = '';
          this.newCustomer.full_name = '';
          this.newCustomer.isOpened = false;
        });
    },

    save(itemClone, item) {
      this.$requestHelpers
        .patch(`api/admin/customer/${item.id}`, itemClone)
        .then((response) => {
          if (response.data.success) {
            Object.assign(item, response.data.result);

            this.setSnack({
              color: 'green',
              text: 'saved',
            });
          }
        });
    },

    remove(id) {
      this.$confirm('Are you sure you want to delete this customer?').then((res) => {
        if (!res) return;

        this.$requestHelpers
          .delete(`api/admin/customer/${id}`)
          .then((response) => {
            if (response.data.success) {
              this.setSnack({
                color: 'green',
                text: 'deleted',
              });
              this.customers = this.customers.filter((e) => e.id !== id);
            }
          });
      });
    },

    getAnalyticsCustomers() {
      this.$requestHelpers
        .get('api/admin/customer/applicationAnalyticsCustomersList')
        .then((response) => {
          this.applicationAnalyticsCustomersList = response.data;
        });
    },
  },

  computed: {
    headers() {
      return [
        {
          text: 'id',
          value: 'id',
          width: '205px',
        },
        {
          text: 'name',
          value: 'name',
          edit: { type: 'text', emptyPlaceholder: true },
        },
        {
          text: 'full name',
          value: 'full_name',
          edit: { type: 'text', emptyPlaceholder: true },
        },
        {
          text: 'RM database',
          value: 'routemap',
          filter: { type: 'select', items: ['empty', ...this.routemapList] },
          edit: {
            type: 'select',
            items: this.routemapList,
            clearable: true,
            emptyPlaceholder: true,
          },
        },
        {
          text: 'Application',
          value: 'application_id',
          filter: {
            type: 'select',
            items: [{ application_id: '0', name: 'empty' }, ...this.applicationsList],
            itemText: 'name',
            itemValue: 'application_id',
          },
          edit: {
            type: 'select',
            items: this.applicationsList,
            itemText: 'name',
            itemValue: 'application_id',
            clearable: true,
            emptyPlaceholder: true,
          },
        },
        {
          text: 'URL',
          value: 'application.application_url',
          filter: { type: 'disable' },
        },
        {
          text: 'Airline',
          value: 'application.customer_code',
          filter: { type: 'disable' },
        },
        {
          text: 'Analytics customers',
          value: 'analytics',
          filter: { type: 'disable' },
        },
        {
          text: '',
          value: 'deleteItem',
          sortable: false,
          width: '80px',
        },
      ];
    },
  },
};
</script>
